/**
 * NodeServicesCard — sdílená karta správy ACM core služeb na nodu.
 *
 * Použito v:
 *   - acm-self widget ServicesPanel (scope.node = forceNode z ModulePanel)
 *   - mesh-peers NodeDetail (peer nod, nodeName)
 *
 * Architektura: viz wiki `sso-publish-architecture`.
 * Backend single source of truth: catalog network entry (extras.sso_enabled).
 *
 * MCP tools (lokál → mcpCall; peer → callWidgetTool / mesh.rpc_call):
 *   acm-self.services_state / service_public / service_sso
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { callWidgetTool } from '../widgets/scope';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  AsyncStatusToggle,
} from './ui';
import { Globe2, Shield, Loader2, Copy, ExternalLink } from 'lucide-react';
import { Fail2banReactionToggle } from './ui/Fail2banReactionToggle';
import { toast } from 'sonner';

interface AcmServiceInfo {
  id: string;
  label: string;
  port: number;
  desc?: string;
  sso?: boolean;
  public_enabled: boolean;
  published: boolean;
  syncing?: boolean;
  pending_reason?: string | null;
  public_domain: string | null;
  sso_enabled: boolean;
  sso_client_id: string | null;
  sso_registered: boolean;
}

type ToolResult = Record<string, unknown> & {
  ok?: boolean;
  error?: string;
  services?: AcmServiceInfo[];
};

/** Lokálně přímý MCP; na peeru node-aware callWidgetTool (stejný unwrap jako widgety). */
async function servicesCall<T extends ToolResult>(
  nodeName: string | undefined,
  tool: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const r = !nodeName
    ? await api.mcp<T>(tool, args)
    : await callWidgetTool<T>(tool, args, { node: nodeName, identity: null });

  // Tooly často vrací { ok:false, error } bez HTTP chyby — musíme throw,
  // jinak AsyncStatusToggle zůstane na „zpracovává…“ (optimistic čeká na value).
  if (r && typeof r === 'object' && r.ok === false) {
    throw new Error(String(r.error || tool));
  }
  return r;
}

const PENDING_REASON_KEYS: Record<string, string> = {
  kc_unavailable: 'acmSelf.pendingKc',
  cert_pending: 'acmSelf.pendingCert',
  npm_pending: 'acmSelf.pendingNpm',
  unpublish_pending: 'acmSelf.pendingUnpublish',
};

function serviceText(
  t: (key: string, fallback?: string) => string,
  id: string,
  field: 'label' | 'desc',
  fallback?: string,
): string {
  return t(`acmSelf.svc.${id}.${field}`, fallback ?? '');
}

export interface NodeServicesCardProps {
  /** Pokud nodeName není dán → lokální mód (acm-self.* přímo). */
  nodeName?: string;
  /** Volitelný nadpis karty (default: acmSelf.pageTitle / publicAccessTitleNode). */
  title?: string;
}

export function NodeServicesCard({ nodeName, title }: NodeServicesCardProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isLocal = !nodeName;
  const nodeLabel = nodeName ?? t('acmSelf.thisNode', 'tomto nodu');

  const queryKey = isLocal
    ? ['services', 'local']
    : ['services', 'peer', nodeName];

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      servicesCall<{ ok: boolean; services: AcmServiceInfo[] }>(
        nodeName,
        'acm-self.services_state',
        {},
      ),
    staleTime: 5_000,
    refetchInterval: 30_000,
    retry: 1,
  });

  const services = data?.services ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey });

  const togglePublic = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const r = await servicesCall(nodeName, 'acm-self.service_public', { id, enabled });
      // tool_result uvnitř může nést ok:false i když outer ok chybí
      const inner = (r as { tool_result?: { ok?: boolean; error?: string } }).tool_result;
      if (inner && inner.ok === false) {
        throw new Error(String(inner.error || t('acmSelf.publishFailed', 'Publish change failed')));
      }
      return r;
    },
    onSuccess: () => {
      toast.success(t('acmSelf.publishUpdated', 'Publish state updated'));
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || t('acmSelf.publishFailed', 'Publish change failed')),
  });

  // KC dostupnost na STEJNÉM nodu jako služby (ne na UI nodu). Jinak na
  // acm-dev uvidíš SSO toggle i pro MPPACM01 bez Keycloaku a pak padneš
  // na "Žádná alive KC…".
  const { data: kcData, isLoading: kcLoading } = useQuery({
    queryKey: isLocal
      ? ['kc-instances-availability', 'local']
      : ['kc-instances-availability', 'peer', nodeName],
    queryFn: () =>
      servicesCall<{ ok: boolean; count: number; instances?: unknown[] }>(
        nodeName,
        'sso.keycloak_list_instances',
        {},
      ),
    staleTime: 5 * 60_000,
  });
  const kcCount = kcData?.count
    ?? (Array.isArray(kcData?.instances) ? kcData!.instances!.length : 0);
  const kcAvailable = kcCount > 0;

  const toggleSso = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const r = await servicesCall(nodeName, 'acm-self.service_sso', { id, enabled }) as {
        ok?: boolean;
        error?: string;
        sso_registered?: boolean;
        sso_enabled?: boolean;
        tool_result?: {
          ok?: boolean;
          error?: string;
          sso_result?: { ok?: boolean; error?: string };
        };
      };
      if (r && typeof r === 'object' && r.ok === false) {
        throw new Error(String(r.error || t('acmSelf.ssoFailed', 'SSO change failed')));
      }
      // Starší nody vrací outer ok:true i když KC registrace selhala
      const ssoRes = r.tool_result?.sso_result;
      if (enabled && ssoRes && ssoRes.ok === false) {
        throw new Error(
          String(ssoRes.error || t('acmSelf.ssoRegisterFailed', 'SSO registration in Keycloak failed')),
        );
      }
      if (enabled && r.sso_enabled && !r.sso_registered) {
        throw new Error(
          String(r.error || t(
            'acmSelf.ssoFlagNoClient',
            'SSO flag is on, but the Keycloak client could not be created.',
          )),
        );
      }
      return r;
    },
    onSuccess: (r) => {
      toast.success(
        r?.sso_registered
          ? t('acmSelf.ssoRegistered', 'SSO registered in Keycloak')
          : t('acmSelf.ssoChanged', 'SSO state changed'),
      );
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || t('acmSelf.ssoFailed', 'SSO change failed')),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Globe2}>
          {title ?? (isLocal
            ? t('acmSelf.pageTitle', 'Public access')
            : t('acmSelf.publicAccessTitleNode', 'Public access — {{node}}', { node: nodeName }))}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        {isLoading && (
          <div className='py-6 text-center text-sm text-gray-500'>
            <Loader2 size={14} className='inline animate-spin mr-2' />
            {t('common.loading', 'Loading…')}
          </div>
        )}
        {error && (
          <div className='py-3 text-sm text-red-400 border border-red-900/40 rounded-xl px-3 bg-red-950/20'>
            <p className='font-medium'>
              {nodeName
                ? t('acmSelf.loadErrorNode', 'Cannot load services on {{node}}', { node: nodeName })
                : t('acmSelf.loadError', 'Cannot load services')}
            </p>
            <p className='text-xs mt-1 text-red-300/80'>{(error as Error).message}</p>
          </div>
        )}
        {!isLoading && !error && services.length === 0 && (
          <div className='py-4 text-center text-sm text-gray-500'>
            {t('acmSelf.noServices', 'No control-plane services')}
            {isFetching ? ` ${t('acmSelf.refreshing', '(refreshing…)')}` : ''}
          </div>
        )}
        {!isLoading && services.map((svc) => {
          const publicPending =
            togglePublic.isPending && togglePublic.variables?.id === svc.id;
          const ssoPending =
            toggleSso.isPending && toggleSso.variables?.id === svc.id;
          return (
            <div
              key={svc.id}
              className='px-4 py-3 rounded-xl bg-gray-900/40 border border-gray-800'
            >
              <div className='flex items-start gap-3'>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='text-sm font-semibold text-white'>
                      {serviceText(t, svc.id, 'label', svc.label)}
                    </span>
                    <span className='text-[10px] font-mono text-gray-500'>:{svc.port}</span>
                    {svc.sso_registered && (
                      <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400'>
                        SSO
                      </span>
                    )}
                  </div>
                  {svc.desc && (
                    <p className='text-[11px] text-gray-500 mt-0.5'>
                      {serviceText(t, svc.id, 'desc', svc.desc)}
                    </p>
                  )}
                  {svc.public_domain && (
                    <div className='text-[11px] mt-0.5 flex items-center gap-1.5 flex-wrap'>
                      {svc.public_enabled
                        ? (
                          <>
                            <a
                              href={`https://${svc.public_domain}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-400 hover:text-blue-300 font-mono inline-flex items-center gap-1'
                              title={t('acmSelf.openInNewWindow', 'Open in a new window')}
                            >
                              https://{svc.public_domain}
                              <ExternalLink size={10} />
                            </a>
                            <button
                              type='button'
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(
                                    `https://${svc.public_domain}`,
                                  );
                                  toast.success(t('acmSelf.urlCopied', 'URL copied'));
                                } catch {
                                  toast.error(t('acmSelf.copyFailed', 'Copy failed'));
                                }
                              }}
                              title={t('acmSelf.copyUrl', 'Copy URL')}
                              className='text-gray-500 hover:text-gray-300 inline-flex items-center'
                            >
                              <Copy size={11} />
                            </button>
                          </>
                        )
                        : (
                          <span className='text-gray-600 font-mono'>{svc.public_domain}</span>
                        )}
                    </div>
                  )}
                </div>
                <div className='shrink-0 min-w-[160px]'>
                  <AsyncStatusToggle
                    value={svc.public_enabled}
                    actual={svc.published}
                    pending={publicPending}
                    onToggle={(v) => togglePublic.mutateAsync({ id: svc.id, enabled: v })}
                    onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                    renderIdleBadge={(on) =>
                      on
                        ? (
                          <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400'>
                            {t('acmSelf.published', 'published')}
                          </span>
                        )
                        : null}
                  />
                </div>
              </div>

              {svc.sso && svc.public_enabled && (
                <div className='mt-2 pl-1 space-y-1'>
                  {kcLoading
                    ? (
                      <div className='text-[10px] text-gray-500 flex items-center gap-1'>
                        <Loader2 size={10} className='animate-spin' />
                        {t('acmSelf.checkingSso', 'Ověřuji SSO na {{node}}…', {
                          node: nodeLabel,
                        })}
                      </div>
                    )
                    : (
                      <AsyncStatusToggle
                        value={svc.sso_enabled}
                        actual={svc.sso_registered}
                        pending={ssoPending}
                        onToggle={(v) => toggleSso.mutateAsync({ id: svc.id, enabled: v })}
                        onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                        renderIcon={(on) => (
                          <Shield size={10} className={on ? 'text-blue-400' : 'text-gray-600'} />
                        )}
                        label={
                          <span className='text-[10px] text-gray-400'>
                            Keycloak SSO{' '}
                            {svc.sso_registered && svc.sso_client_id
                              ? `(${svc.sso_client_id})`
                              : !kcAvailable
                              ? t('acmSelf.ssoViaHub', '(přes centrálu)')
                              : ''}
                          </span>
                        }
                      />
                    )}
                </div>
              )}

              {svc.pending_reason && (
                <div className='mt-1 text-[10px] text-yellow-500'>
                  {PENDING_REASON_KEYS[svc.pending_reason]
                    ? t(PENDING_REASON_KEYS[svc.pending_reason], svc.pending_reason)
                    : svc.pending_reason}
                </div>
              )}
            </div>
          );
        })}
        {isLocal && !isLoading && <Fail2banReactionToggle variant='service-row' />}
      </CardContent>
    </Card>
  );
}
